import { useQuery } from "@apollo/client";
import { useI18n } from "react-simple-i18n";
import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { styled } from "@mui/material/styles";
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { Box, Typography, Modal, Rating, Skeleton } from "@mui/material";
import DownloadIcon from '@mui/icons-material/Download';
import SendIcon from '@mui/icons-material/Send';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArticleIcon from '@mui/icons-material/Article';
import LanguageIcon from '@mui/icons-material/Language';
import CheckIcon from '@mui/icons-material/Check';
import Avatar from '@mui/material/Avatar';

import { capitalizeFirstLetter, getTimeStamp, toastMessage } from "../utils";
import { restApi } from "../provider/restApi";
import { useGlobalContext } from "../provider";
import { tempAvatarImg } from "../assets/image";
import { Layout } from "../components/layout/layout";
import { TextField } from "../components/inputs/textfield";
import { GlobalFirstSection, GlobalSpacing } from "../components/layout/styles";
import { FirstButton, TransparentButton } from "../components/buttons/buttons";
import { apiNotification } from "../services/apiNotification";
import { CustomRating } from "../components/inputs/rating";
import { EditCourse } from "./components/editCourse";
import { GET_COURSEDetail } from "../components/graphql";
import { PageNotFound } from "./404";
import { PaymentForm } from "./components/paymentForm";
import { useCustomNavigate } from "../provider/navigate";
import { config } from "../config/config";

interface StatusObject {
	review: string
	reviewScore: number
	question: string

	errorReview: boolean
	errorScore: boolean
	errorQuestion: boolean
}

const initStatus: StatusObject = {
	review: "",
	reviewScore: 0,
	question: "",

	errorReview: false,
	errorScore: false,
	errorQuestion: false,
}

const emptyArray = new Array(6).fill(0);
const stripePromise = loadStripe(config.stripe.publishkey);


const CourseDetails = () => {
	const { t } = useI18n();
	const params = useParams(); // Get the 'id' from the URL
	const [isVideoOpen, setIsVideoOpen] = useState(false);
	const [updateDetail, setUpdateDetail] = useState(false)
	const [globalStatus, { dispatch }]: GlobalContextType = useGlobalContext();
	const [openSubscribeModal, setOpenSubscribeModal] = useState(false);
	const [status, setStatus] = useState<StatusObject>(initStatus);
	const { navigate } = useCustomNavigate();

	const updateStatus = (data: Partial<StatusObject>) => {
		setStatus({ ...status, ...data });
	}

	const { data: courseData, loading, error } = useQuery(GET_COURSEDetail, {
		variables: { id: params.id || '' },
		pollInterval: 1000,
	})

	const courseDetailData: CourseDetailObject = useMemo(() => {
		if (!!loading || !!error || !params.id) return null;
		return courseData.getCourseData;
	}, [courseData, loading, error, params.id])

	if (!!error || !params.id) {
		return (
			<PageNotFound />
		)
	}

	const validateReview = () => {
		if (!status.review) return false;
		if (!status.reviewScore) return false;
		return true;
	}

	const validateQuestion = () => {
		if (!status.question) return false;
		return true;
	}

	const handleDownload = () => {
		if (!courseDetailData.isSubscribe) return;
		// Implement video download logic
		const link = document.createElement('a');
		link.href = courseDetailData.lectureVideo;
		link.download = `${courseDetailData.title}.mp4`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	}

	const onSubmitReview = async () => {
		try {
			if (!courseDetailData.subscribed || !courseDetailData.isReview) return;

			const validate = validateReview();
			if (!validate) {
				updateStatus({
					errorReview: !status.review,
					errorScore: !status.errorScore,
				})

				return false;
			}

			dispatch({ loading: true });
			await restApi.submitReview({
				review: status.review,
				id: courseDetailData.id,
				reviewScore: status.reviewScore,
			})

			setUpdateDetail(!updateDetail);
			dispatch({ loading: false });
			toastMessage("success", "Review submitted!");
		} catch (err: any) {
			dispatch({ loading: false });
			apiNotification(err, "Submit review failed!");
		}
	}

	const onSubmitQuestion = async () => {
		try {
			if (!courseDetailData.subscribed) return;

			const validate = validateQuestion();
			if (!validate) {
				updateStatus({ errorQuestion: !status.question })
				return false;
			}

			dispatch({ loading: true });
			await restApi.submitQuestion({
				id: courseDetailData.id, question: status.question,
			})

			setUpdateDetail(!updateDetail);
			dispatch({ loading: false });
			updateStatus({ question: "", errorQuestion: false });
			toastMessage("success", "Question submitted!");
		} catch (err: any) {
			dispatch({ loading: false });
			apiNotification(err, "Question Submit failed!");
		}
	}

	// const handleDeleteCourse = async (courseId: string) => {
	// 	try {
	// 		dispatch({ loading: true });
	// 		await restApi.deleteCourse(courseId);
	// 		dispatch({ loading: false });
	// 		toastMessage("success", "Course deleted successfully!");
	// 		navigate('/courses'); // Redirect to courses list or another appropriate page
	// 	} catch (err: any) {
	// 		dispatch({ loading: false });
	// 		apiNotification(err, "Course deletion failed!");
	// 	}
	// };

	return (
		<Layout>
			{!!loading && (
				<CourseDetailsWrapper>
					<HeroSection>
						<GlobalSpacing>
							<HeroContent>
								<Skeleton variant="rectangular" height={25} />
								<Skeleton variant="rectangular" height={50} />

								<CourseStats>
									<Skeleton variant="rounded" height={25} width={100} />
									<Skeleton variant="rounded" height={25} width={100} />
									<Skeleton variant="rounded" height={25} width={100} />
								</CourseStats>
							</HeroContent>
						</GlobalSpacing>
					</HeroSection>

					<GlobalSpacing>
						<ContentWrapper>
							<MainContent>
								<CourseSection>
									<Skeleton variant="rectangular" height={25} width={200} />
									<Skeleton variant="rectangular" height={50} />
								</CourseSection>

								<CourseSection>
									<Skeleton variant="rectangular" height={25} width={200} />

									<LearningPoints>
										{emptyArray.map((i, key: number) => (
											<LearningPoint key={key}>
												<Skeleton variant="rectangular" height={20} width="100%" />
											</LearningPoint>
										))}
									</LearningPoints>
								</CourseSection>
							</MainContent>

							<SideContent>
								<CourseCard>
									<VideoPreview>
										<Skeleton variant="rectangular" width="100%" height="100%" />
									</VideoPreview>
								</CourseCard>
							</SideContent>
						</ContentWrapper>
					</GlobalSpacing>
				</CourseDetailsWrapper>
			)}

			{!loading && (
				<React.Fragment>
					<CourseDetailsWrapper>
						<HeroSection>
							<GlobalSpacing>
								<HeroContent>
									<Typography variant="h3" component="h1">
										{courseDetailData.title}
									</Typography>

									<Typography variant="subtitle1" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
										{courseDetailData.description}
									</Typography>

									<CourseStats>
										<StatItem>
											<AccessTimeIcon />
											<Typography variant="body2">12 Hours</Typography>
										</StatItem>

										<StatItem>
											<ArticleIcon />
											<Typography variant="body2">15 Lessons</Typography>
										</StatItem>

										<StatItem>
											<LanguageIcon />
											<Typography variant="body2">
												{courseDetailData.language === 'jp' ? "Japanese" : "English"}
											</Typography>
										</StatItem>
									</CourseStats>
								</HeroContent>
							</GlobalSpacing>
						</HeroSection>

						<GlobalSpacing>
							<ContentWrapper>
								<MainContent>
									<CourseSection>
										<Typography variant="h5">
											{t("courseDetails.aboutCourse")}
										</Typography>

										<Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
											{courseDetailData.description}
										</Typography>
									</CourseSection>

									<CourseSection>
										<Typography variant="h5">{t("courseDetails.whatYouWillLearn")}</Typography>

										<LearningPoints>
											{courseDetailData.learnOutcomes.map((data: string, key: number) => (
												<LearningPoint key={key}>
													<CheckIcon fontSize="small" />
													<Typography>{data}</Typography>
												</LearningPoint>
											))}
										</LearningPoints>
									</CourseSection>

									{(courseDetailData.subscribed || courseDetailData.isOwner) && (
										<CourseSection>
											<Typography variant="h5">{t("courseDetails.questionanswer")}</Typography>

											{/* Q&A History */}
											<QAHistory>
												{courseDetailData.questionAnswers.map((item: QuestionAnswersObject, key: number) => (
													<QAItem key={key}>
														<QuestionSection>
															<UserInfo>
																<Avatar src={item.user.image} alt={capitalizeFirstLetter(item.user.name)} />

																<Box flexDirection="column">
																	<Typography variant="body2">{capitalizeFirstLetter(item.user.name)}</Typography>
																	<Typography variant="subtitle1" component="label">{getTimeStamp(item.questionTime)}</Typography>
																</Box>
															</UserInfo>

															<Typography variant="body1">{item.question}</Typography>
														</QuestionSection>

														{!!item.answer && (
															<AnswerSection>
																<UserInfo>
																	<Avatar src={courseDetailData.owner.image} alt={capitalizeFirstLetter(courseDetailData.owner.name)} />

																	<Box flexDirection="column">
																		<Typography variant="body2">{capitalizeFirstLetter(courseDetailData.owner.name)}</Typography>
																		<Typography variant="subtitle1" component="label">{getTimeStamp(item.answerTime)}</Typography>
																	</Box>
																</UserInfo>

																<Typography variant="body1">{item.answer}</Typography>
															</AnswerSection>
														)}

														{(courseDetailData.isOwner && !item.answer) && (
															<AnswerForm courseDetailData={courseDetailData} QAdata={item} setUpdateDetail={setUpdateDetail} updateDetail={updateDetail} />
														)}
													</QAItem>
												))}
											</QAHistory>

											{/* Ask Question Form */}
											{!courseDetailData.isOwner && (
												<FormContainer>
													<TextField multiline rows={3}
														placeholder="Ask a question about this course..."
														onChange={({ target }: any) => { updateStatus({ question: target.value, errorQuestion: false }) }}
														error={status.errorQuestion}
														value={status.question}
													/>

													<FirstButton onClick={onSubmitQuestion}>
														<Typography variant="body1">{t("buttons.askQuestion")}</Typography>
														<SendIcon fontSize="small" />
													</FirstButton>
												</FormContainer>
											)}
										</CourseSection>
									)}

									<CourseSection>
										<Typography variant="h5">{t("courseDetails.reviews")}</Typography>

										{/* Reviews History */}
										<ReviewHistory>
											{courseDetailData.reviews.map((item: ReviewObject, key: number) => (
												<ReviewItem key={key}>
													<UserInfo>
														<Avatar src={item.image || tempAvatarImg} alt={capitalizeFirstLetter(item.name)} />

														<Box sx={{ flex: 1 }}>
															<Typography variant="body2">{capitalizeFirstLetter(item.name)}</Typography>
															<Typography variant="subtitle1" component="label">
																{getTimeStamp(item.created)}
															</Typography>
														</Box>

														<Rating value={item.reviewScore} precision={0.5} readOnly size="small" />
													</UserInfo>

													<Typography variant="body2">{item.message}</Typography>
												</ReviewItem>
											))}
										</ReviewHistory>

										{/* Review Form */}
										{(courseDetailData.subscribed && courseDetailData.isReview && !courseDetailData.isOwner) && (
											<FormContainer>
												<TextField multiline rows={3}
													placeholder="Write your review..."
													onChange={({ target }: any) => { updateStatus({ review: target.value, errorReview: false }) }}
													error={status.errorReview}
													value={status.review}
												/>

												<CustomRating label="Review Score"
													onChange={(value: number) => { updateStatus({ reviewScore: value, errorScore: false }) }}
													value={status.reviewScore} error={status.errorReview}
												/>

												<FirstButton fullWidth onClick={onSubmitReview}>
													<Typography variant="body1">{t("buttons.submitReview")}</Typography>
													<SendIcon fontSize="small" />
												</FirstButton>
											</FormContainer>
										)}
									</CourseSection>
								</MainContent>

								<SideContent>
									<CourseCard>
										<VideoPreview onClick={() => setIsVideoOpen(true)}>
											<video muted preload="auto"
												src={courseDetailData.lectureVideo}
												onAbort={() => console.log("Video loading was aborted")}
												onError={(e: any) => console.log("Error loading video:", e.message)}
												disablePictureInPicture
											/>
											<PlayOverlay>
												<PlayArrowIcon fontSize="large" />
											</PlayOverlay>
										</VideoPreview>

										<Elements stripe={stripePromise}>
                    <CardContent>
                        {globalStatus.loginStatus &&
                          courseDetailData.isSubscribe &&
                          !courseDetailData.subscribed &&
                          !courseDetailData.isOwner && (
                            <>
                              <FirstButton
                                onClick={() => {
                                  setOpenSubscribeModal(true);
                                }}
                              >
                                <Typography variant="body1">
                                  {t("buttons.subscribe")} ${courseDetailData.price}
                                </Typography>
                              </FirstButton>

                              <PaymentForm
                                data={courseDetailData}
                                open={openSubscribeModal}
                                onCancel={() => {
                                  setOpenSubscribeModal(false);
                                }}
                              />
                            </>
                          )}
                      </CardContent>

                      </Elements>

                    {/* <CardContent>
                      {globalStatus.loginStatus &&
                        courseDetailData.isSubscribe &&
                        !courseDetailData.subscribed &&
                        !courseDetailData.isOwner && (
                          <Elements stripe={stripePromise}>
                            <FirstButton
                              onClick={() => {
                                setOpenSubscribeModal(true);
                                // setShowSubscribeButton(true);
                              }}
                              // style={{display: showSubscribeButton?'block':'none'}}
                            >
                              <Typography variant="body1">
                                {t("buttons.subscribe")} $
                                {courseDetailData.price}
                              </Typography>
                            </FirstButton>

                            <PaymentForm
                              data={courseDetailData}
                              open={openSubscribeModal}
                              onCancel={() => {
                                setOpenSubscribeModal(false);
                                // setShowSubscribeButton(true);
                              }}
                              // onClientSecretAvailable={() => setShowSubscribeButton(false)}
                            />
                          </Elements>
                        )}
                    </CardContent> */}
                  </CourseCard>

									{courseDetailData.isOwner && (
										<>
											<EditCourse data={courseDetailData}
												setUpdateDetail={setUpdateDetail}
												updateDetail={updateDetail}
											/>
											{/* <FirstButton onClick={() => handleDeleteCourse(courseDetailData.id)}>
												<Typography variant="body1">{t("buttons.delete")}</Typography>
											</FirstButton> */}
										</>
									)}
								</SideContent>
							</ContentWrapper>
						</GlobalSpacing>
					</CourseDetailsWrapper>

					<Modal open={isVideoOpen}
						onClose={() => setIsVideoOpen(false)}
					>
						<VideoModal>
							<video controls preload="auto"
								src={courseDetailData.lectureVideo}
								onAbort={() => console.log("Video loading was aborted")}
								onError={(e: any) => console.log("Error loading video:", e.message)}
								disablePictureInPicture
							/>
							{courseDetailData.subscribed && (
								<DownloadButton startIcon={<DownloadIcon />} onClick={handleDownload}>
									<Typography variant="body1">Download</Typography>
								</DownloadButton>
							)}
						</VideoModal>
					</Modal>
				</React.Fragment>
			)}
		</Layout>
	)
}

interface AnswerFormProps {
	QAdata: QuestionAnswersObject
	courseDetailData: CourseDetailObject
	setUpdateDetail: (state: boolean) => void
	updateDetail: boolean
}

const AnswerForm = ({ QAdata, courseDetailData, setUpdateDetail, updateDetail }: AnswerFormProps) => {
	const { t } = useI18n();
	const [answer, setAnswer] = useState("");
	const [errorAnswer, setErrorAnswer] = useState(false);
	const [, { dispatch }]: GlobalContextType = useGlobalContext();

	const onSubmitAnswer = async () => {
		try {
			if (!courseDetailData.isOwner) return false;

			if (!answer) {
				setErrorAnswer(false);
				return false;
			}

			dispatch({ loading: true });
			await restApi.submitAnswer({
				id: QAdata.id, courseId: courseDetailData.id, answer: answer,
			})

			setUpdateDetail(!updateDetail);
			dispatch({ loading: false });
			setAnswer(""); setErrorAnswer(false);
			toastMessage("success", "Answer submitted!");
		} catch (err: any) {
			dispatch({ loading: false });
			apiNotification(err, "Answer Submit failed!");
		}
	}

	return (
		<FormContainer>
			<TextField multiline rows={3}
				placeholder="Answer about this question..."
				onChange={({ target }: any) => { setAnswer(target.value); setErrorAnswer(false); }}
				error={errorAnswer}
				value={answer}
			/>

			<FirstButton onClick={onSubmitAnswer}>
				<Typography variant="body1">{t("buttons.answer")}</Typography>
				<SendIcon fontSize="small" />
			</FirstButton>
		</FormContainer>
	)
}

const CourseDetailsWrapper = styled(GlobalFirstSection)(({ theme }) => ({
	display: 'flex',
	flexDirection: "column",
	gap: theme.spacing(1),
	padding: `${theme.spacing(5)} 0px`,

	[theme.breakpoints.down("md")]: {
		padding: `${theme.spacing(3)} 0px`,
	}
}))

const HeroSection = styled(Box)(({ theme }) => ({
	backgroundColor: theme.palette.common.mainBg,
	borderBottom: `1px solid ${theme.palette.common.firstBorder}`,
	paddingBottom: theme.spacing(8),

	[theme.breakpoints.down("md")]: {
		paddingBottom: theme.spacing(7),
	}
}))

const HeroContent = styled(Box)(({ theme }) => ({
	maxWidth: '100%',
	display: "flex",
	flexDirection: "column",
	gap: theme.spacing(1)
}))

const CourseStats = styled(Box)(({ theme }) => ({
	display: 'flex',
	gap: theme.spacing(4),
	marginTop: theme.spacing(1),

	[theme.breakpoints.down("md")]: {
		gap: theme.spacing(2),
		marginTop: theme.spacing(0.5),
	}
}))

const StatItem = styled(Box)(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	gap: theme.spacing(1),
	color: `${theme.palette.common.firstColor}c0`,

	p: {
		color: `${theme.palette.common.firstColor}c0`
	},

	[theme.breakpoints.down("md")]: {
		gap: theme.spacing(0.5),
	}
}))


const ContentWrapper = styled(Box)(({ theme }) => ({
	display: 'grid',
	gridTemplateColumns: '1fr 350px',
	gap: theme.spacing(3),
	marginTop: theme.spacing(-6),

	[theme.breakpoints.down('lg')]: {
		display: "flex",
		flexDirection: "column-reverse",
	},

	[theme.breakpoints.down("md")]: {
		gap: theme.spacing(2),
	}
}))

const MainContent = styled(Box)(({ theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	gap: theme.spacing(3),

	[theme.breakpoints.down("md")]: {
		gap: theme.spacing(2),
	}
}))

const SideContent = styled(Box)(({ theme }) => ({
	top: 80,
	position: 'sticky',
	height: 'fit-content',

	[theme.breakpoints.down("lg")]: {
		position: "static",
	}
}))

const CourseCard = styled(Box)(({ theme }) => ({
	overflow: 'hidden',
	borderRadius: theme.spacing(1),
	backgroundColor: theme.palette.common.mainBg,
	boxShadow: `0px 1px 5px ${theme.palette.common.firstBorder}`,
}))

const VideoPreview = styled(Box)({
	position: 'relative',
	cursor: 'pointer',

	'& video, .MuiSkeleton-root': {
		width: '100%',
		display: 'block',
		objectFit: 'cover',
		aspectRatio: '5/3',
	}
})

const PlayOverlay = styled(Box)(({ theme }) => ({
	position: 'absolute',
	top: '50%', left: '50%',
	transform: 'translate(-50%, -50%)',

	display: "flex",
	flexDirection: "row",
	alignItems: "center",
	justifyContent: "center",

	borderRadius: '50%',
	padding: theme.spacing(2),
	backgroundColor: `${theme.palette.common.firstColor}d0`,
	transition: 'transform 0.2s',
	color: 'white',

	'&:hover': {
		transform: 'translate(-50%, -50%) scale(1.1)',
	},

	[theme.breakpoints.down("md")]: {
		padding: theme.spacing(1),

		svg: {
			transform: "scale(.9)",
		}
	}
}))

const CardContent = styled(Box)(({ theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	padding: theme.spacing(2),

	[theme.breakpoints.down("md")]: {
		padding: theme.spacing(1),
	}
}))


const CourseSection = styled(Box)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	gap: theme.spacing(2),

	padding: theme.spacing(3),
	borderRadius: theme.spacing(1),
	backgroundColor: theme.palette.common.mainBg,
	boxShadow: `0px 1px 5px ${theme.palette.common.firstBorder}`,

	h5: {
		fontWeight: 600,
	},

	[theme.breakpoints.down("md")]: {
		gap: theme.spacing(1),
		padding: theme.spacing(2),
	}
}))

const LearningPoints = styled(Box)(({ theme }) => ({
	display: 'grid',
	gap: theme.spacing(2),
	gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',

	[theme.breakpoints.down("md")]: {
		gap: theme.spacing(1),
	}
}))

const LearningPoint = styled(Box)(({ theme }) => ({
	display: 'flex',
	alignItems: 'flex-start',
	gap: theme.spacing(1),

	'& .MuiSvgIcon-root': {
		marginTop: '2px',
		color: theme.palette.common.thirdColor,
	},

	'& .MuiTypography-root': {
		color: `${theme.palette.common.firstColor}c0`,
	},
}))


const QAHistory = styled(Box)(({ theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	gap: theme.spacing(3),
	marginBottom: theme.spacing(2),

	[theme.breakpoints.down("md")]: {
		gap: theme.spacing(2),
		marginBottom: theme.spacing(1),
	}
}))

const QAItem = styled(Box)(({ theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	gap: theme.spacing(2),
	padding: theme.spacing(2),

	borderRadius: 8,
	backgroundColor: theme.palette.common.mainBg,
	border: `1px solid ${theme.palette.common.firstBorder}`,

	[theme.breakpoints.down("md")]: {
		gap: theme.spacing(1),
		padding: theme.spacing(1.5),
	}
}))

const QuestionSection = styled(Box)(({ theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	gap: theme.spacing(1),
}))

const UserInfo = styled(Box)(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	gap: theme.spacing(1.5),
	cursor: "pointer",

	p: {
		fontWeight: 500,
		lineHeight: "1.2rem",
	},

	label: {
		cursor: "pointer",
		lineHeight: "1rem",
		color: `${theme.palette.common.firstColor}c0`,
	},
}))

const AnswerSection = styled(Box)(({ theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	padding: theme.spacing(2),
	marginLeft: theme.spacing(2),

	borderRadius: theme.spacing(1),
	backgroundColor: `${theme.palette.common.thirdColor}`,

	p: {
		color: theme.palette.common.secondColor,
	},

	label: {
		color: `${theme.palette.common.secondColor}c0 !important`,
	},

	[theme.breakpoints.down("md")]: {
		marginLeft: theme.spacing(0),
		padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
	}
}))

const FormContainer = styled(Box)(({ theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	gap: theme.spacing(2),

	[theme.breakpoints.down("md")]: {
		gap: theme.spacing(1),
	}
}))

const ReviewHistory = styled(Box)(({ theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	gap: theme.spacing(3),
	marginBottom: theme.spacing(2),

	[theme.breakpoints.down("md")]: {
		gap: theme.spacing(2),
		marginBottom: theme.spacing(1),
	}
}))

const ReviewItem = styled(Box)(({ theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	gap: theme.spacing(2),
	padding: theme.spacing(2),

	borderRadius: 8,
	backgroundColor: theme.palette.common.mainBg,
	border: `1px solid ${theme.palette.common.firstBorder}`,

	[theme.breakpoints.down("md")]: {
		gap: theme.spacing(1),
		padding: theme.spacing(1.5),
	}
}))

const VideoModal = styled(Box)(({ theme }) => ({
	position: 'absolute',
	top: '50%', left: '50%',
	transform: 'translate(-50%, -50%)',
	width: '90vw',
	maxWidth: "1280px",
	maxHeight: "720px",
	backgroundColor: theme.palette.common.mainBg,
	borderRadius: theme.spacing(1),
	padding: theme.spacing(2),
	outline: 'none',

	video: {
		width: '100%',
		aspectRatio: "16/9",
	    objectFit: "contain",
		borderRadius: theme.spacing(1),
	},

	[theme.breakpoints.down("md")]: {
		padding: theme.spacing(1),
		borderRadius: theme.spacing(0.5),

		video: {
			borderRadius: theme.spacing(0.5),
		}
	}
}))

const DownloadButton = styled(TransparentButton)(({ theme }) => ({
	position: 'absolute',
	right: theme.spacing(2.5),
	bottom: theme.spacing(2.5),
	borderRadius: theme.spacing(0),
	backgroundColor: `${theme.palette.common.firstColor}50`,

	p: {
		color: `${theme.palette.common.secondColor}90`,
	},

	'&:hover': {
		backgroundColor: `${theme.palette.common.firstColor}80`,
	},

	[theme.breakpoints.down("md")]: {
		right: theme.spacing(1.5),
		bottom: theme.spacing(1.5),

		p: {
			display: "none",
		}
	}
}))

export { CourseDetails };